import { Draggable as DraggableEvent } from "@fullcalendar/interaction";
import { useEffect, useState } from "react";
import { AppointmentDto, ContinuousAppointmentDto, ContinuousEventTimeSlotDto, NoteDto, PatientDto, TherapistListType, TimeSlotDto } from "../../../api-client";
import { Badge, Box, Divider, Fade, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import MapIcon from "@mui/icons-material/Map";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { TherapySlotRow } from "../../TherapySlotRow";
import { AppointmentTime } from "../../AppointmentTime";
import { observer } from "mobx-react";
import CloseIcon from "@mui/icons-material/Close";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import { useEventShelfController } from "./EventShelfController";
import { DynamicTemplateProvider, useDynamicTemplateProviderController } from "../DynamicTemplateProvider";
import { adaptAppointmentToDraggableEvent, adaptTimeSlotToDraggableEvent, isCancelledAppointment } from "../controller/adapters";
import {
  useTermDefaultDurationLookup,
  useTermDefaultPassivityLookup,
} from "../../../stores/MasterTables/masterTables.queries";
import { formatDateDisplay, formatTime } from "../../../helpers/dateFormat";
import { MobiliAvatar } from "../../../atoms/MobiliAvatar";
import useStore from "../../../helpers/useStore";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";
import ShelfAppointmentMenu from "./ShelfAppointmentMenu";
import ShelfSection from "./ShelfSection";
import { closestTargetForCard } from "./closestTarget";
import { mobiliTheme } from "../../../themes/mobiliTheme";
import {
  canonicalTherapyKey,
  slotTherapyList,
  slotsInChronologicalOrder,
} from "../../../helpers/slotTherapy";
import { cardShell } from "../../cardShell";

// Internal props for individual shelf instances (not exposed to parent)
interface ShelfInstanceProps {
  appointments: (AppointmentDto | ContinuousEventTimeSlotDto)[] | ContinuousAppointmentDto[];
  title?: string;
  patient?: PatientDto;
  draggable?: boolean;
  closeButton?: boolean;
  mode?: "pinboard" | "unscheduled";
  /**
   * True while the calendar is in Dauertermin (continuous) view: the cards then
   * hold ContinuousAppointmentDtos, and the closest-appointments search must
   * look at the continuous plane. The whole band follows the calendar's view —
   * both sections show the plane the grid is showing.
   */
  isContinuous?: boolean;
  /** Fixed card-row height dictated by the band. */
  rowHeight: number;
  /** Let the section take remaining band width. */
  grow?: boolean;
  /** Column-band mode: cards wrap instead of scrolling horizontally. */
  wrap?: boolean;
  onSelectEvent: (eventId: string) => void;
  onClose?: () => void;
}

/**
 * Label for the floating drag ghost. FullCalendar's drag mirror is a deep
 * CLONE of the dragged element — a torn-off slot row for body drags, a bare
 * glyph for grip drags — so every drag surface embeds this label in a hidden
 * `.shelf-drag-ghost` node that only the mirror clone reveals (Calendar.css),
 * following the same reveal-in-mirror pattern already used for the drag
 * start/end times on internal event drags.
 */
const dragGhostLabel = (slots: any[], patient?: PatientDto) => {
  const abbrevs = slotTherapyList(slots, " + ");
  return [abbrevs || "Termin", patient?.lastName].filter(Boolean).join(" · ");
};

/**
 * One FullCalendar external drag source. The band's single Draggable matches
 * `.fc-event` descendants, so every surface rendered through this component is
 * independently draggable with the payload it carries.
 *
 * Drag zones follow "grab the thing to move the thing": the card's CONTENT is
 * the whole-appointment surface — each slot row's body carries the
 * whole-appointment payload, so grabbing anywhere on the card moves all of it —
 * and the small grips beside the rows are the exception that lifts a single
 * slot out of a multi-slot appointment. (Originally inverted: only a left-rail
 * grip dragged the appointment, and users had to aim for it.)
 *
 * Surfaces are SIBLINGS, never nested: two `.fc-event`s in an ancestor chain
 * would both match the band's Draggable and fight over the pointer, so a row is
 * `[slot grip][row body]` side by side, each with its own payload.
 */
const DragSurface: React.FC<{
  payload: unknown;
  /** Ghost-card label revealed in the drag mirror (Calendar.css). */
  ghost: string;
  onClick?: () => void;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}> = ({ payload, ghost, onClick, style, children }) => (
  <div
    className="fc-event fc-h-event fc-daygrid-event fc-daygrid-block-event shelf-drag-source"
    onClick={onClick}
    data-event={JSON.stringify(payload)}
    style={{
      cursor: "grab",
      // .fc-h-event ships FullCalendar's event-blue background and border;
      // inline so no specificity battle. The card provides the surface.
      background: "transparent",
      color: "inherit",
      border: "none",
      boxShadow: "none",
      margin: 0,
      padding: 0,
      ...style,
    }}
  >
    {children}
    <span className="shelf-drag-ghost">{ghost}</span>
  </div>
);

// Individual shelf instance component
const ShelfInstance = observer((props: ShelfInstanceProps) => {
  const { appointments, patient, title, draggable, closeButton, mode, isContinuous, rowHeight, grow, wrap, onSelectEvent, onClose } = props;
  const CalendarStore = useStore(CalendarContext);
  // Provisional lengths for term-only stubs — master-data default per
  // (term x tarif group); the backend still has the final word post-resolution.
  const termDefaultDuration = useTermDefaultDurationLookup();
  // And the same default's passivity, so a Dauertermin template's ghost leaves
  // a leading Warmpackung out of the drop point like a concrete stub does.
  const termDefaultPassivity = useTermDefaultPassivityLookup();
  // Appointment-level selection lives in CalendarStore (selectedShelfAppointmentId)
  // so the outline, the closest-appointments modal and the keyboard "belegen"
  // insert can never drift apart.
  // Slot-level selection is independent of appointment-level selection: clicking a
  // slot handle outlines just that slot, clicking the appointment handle outlines
  // the whole card. Selecting one clears the other.
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);

  // NOTE: the FullCalendar Draggable for the whole band (template chips +
  // shelf card grips) is registered ONCE by the parent EventShelf component;
  // shelf instances only render `.fc-event` grips with data-event payloads.

  const groupAppointmentsByRx = (appointments: (AppointmentDto | ContinuousEventTimeSlotDto)[] | ContinuousAppointmentDto[]) => {
    const groupedAppointments: Record<string, AppointmentDto[]> = {};
    const assignedAppointments = new Set<string>();
  
    // Step 1: First, group all cancelled appointments (priority grouping)
    appointments?.forEach((appointment) => {
      const therapyRxIds = canonicalTherapyKey((appointment as AppointmentDto)?.timeSlots);
  
      const addressId = (appointment as AppointmentDto).address?.id || 'unknown-address';
      // Cancellation identity is originalDate + unresolved (the shared
      // predicate) — NOT byTherapist: patient-initiated cancellations get the
      // cancelled rendering too, the flag only labels the origin.
      const isCancelled = !!appointment && isCancelledAppointment(appointment as AppointmentDto);
  
      const cancelledKey = `cancelled-${therapyRxIds}-${addressId}`;
  
      // Check if appointment is already assigned
      if (assignedAppointments.has(appointment.id as string)) return;
  
      // Group cancelled appointments first
      if (isCancelled) {
        if (!groupedAppointments[cancelledKey]) {
          groupedAppointments[cancelledKey] = [];
        }
        groupedAppointments[cancelledKey].push((appointment as AppointmentDto));
        assignedAppointments.add(appointment.id as string); // Mark as grouped
      }
    });
  
    // Step 2: Now, group active appointments (skip those already assigned)
    appointments?.forEach((appointment) => {
      const therapyRxIds = canonicalTherapyKey((appointment as AppointmentDto)?.timeSlots);
  
      const addressId = (appointment as AppointmentDto).address?.id || 'unknown-address';
      // Cancellation identity is originalDate + unresolved (the shared
      // predicate) — NOT byTherapist: patient-initiated cancellations get the
      // cancelled rendering too, the flag only labels the origin.
      const isCancelled = !!appointment && isCancelledAppointment(appointment as AppointmentDto);
  
      const activeKey = `${therapyRxIds}-${addressId}`;
  
      // Check if appointment is already assigned to a group
      if (assignedAppointments.has(appointment.id as string)) return;
  
      // Group active appointments
      if (!isCancelled) {
        if (!groupedAppointments[activeKey]) {
          groupedAppointments[activeKey] = [];
        }
        groupedAppointments[activeKey].push((appointment as AppointmentDto));
        assignedAppointments.add(appointment.id as string); // Mark as grouped
      }
    });
  
    // console.log('Grouped Appointments:',groupedAppointments);
    return groupedAppointments;
  };
  
  // Build grouped appointment structure only for Ablage; otherwise each appointment is its own group
  let groupAppointments: Record<string, AppointmentDto[]> = {};
  let ablageAppointments: AppointmentDto[] = [];
  let totalLength = 0;
  if (mode === "unscheduled") {
    groupAppointments = groupAppointmentsByRx(appointments);
    totalLength = Object.values(groupAppointments).reduce((total, group) => total + group.length, 0);    
  } else {
    ablageAppointments = JSON.parse(JSON.stringify(appointments)) as AppointmentDto[];
    totalLength = ablageAppointments.length;
  }
  // console.log('Grouped Appointments for rendering:', groupAppointments);
  // console.log('Ablage Appointments for rendering:', ablageAppointments);
  
  const handleEventClick = (appId: string) => {
    setSelectedSlotId(null); // appointment selection clears any slot selection
    return onSelectEvent(appId); // toggles CalendarStore.selectedShelfAppointmentId
  }

  const handleSlotClick = (slotId: string) => {
    setSelectedSlotId((prev) => (prev === slotId ? null : slotId));
  };

  const sectionTitle = [title, patient?.lastName].filter(Boolean).join(" · ");

  return (
    <ShelfSection
      title={sectionTitle}
      count={totalLength || 0}
      rowHeight={rowHeight}
      grow={grow}
      wrap={wrap}
      headerExtras={
        closeButton && onClose ? (
          <Tooltip title="Ablage schließen" placement="top">
            <IconButton size="small" onClick={onClose} sx={{ p: 0.25 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : undefined
      }
    >
      {appointments.length === 0 && (
        <Typography variant="caption" sx={{ opacity: 0.7, alignSelf: "center" }}>
          Kein Termin
        </Typography>
      )}
            {(() => {
              // Floating action that appears on selected shelf items (like SpeedDial).
              // Carries the card's OWN search target rather than letting the modal
              // guess from the selection: the id a card holds differs per plane,
              // and a slot pin's own id is a timeslot, not an appointment.
              const renderSelectionAction = (
                isSelected: boolean,
                appointment: AppointmentDto,
              ) => (
                <Fade in={isSelected}>
                  <Tooltip
                    title={isContinuous ? "Nächstgelegene Dauertermine" : "Nächstgelegene Termine"}
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        CalendarStore.openClosestAppointments(
                          closestTargetForCard(appointment, !!isContinuous)
                        );
                      }}
                      sx={{
                        position: "absolute",
                        bottom: 2,
                        right: 2,
                        zIndex: 10,
                        bgcolor: "secondary.main",
                        color: "secondary.contrastText",
                        boxShadow: 2,
                        width: 24,
                        height: 24,
                        "&:hover": { bgcolor: "secondary.dark" },
                      }}
                    >
                      <MapIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Fade>
              );

              // Render helpers extracted from inline map for clarity & conditional customization
              const renderDefaultGroups = (groups: Record<string, AppointmentDto[]>) =>
                Object.entries(groups).map(([groupKey, appts]) => {
                  if (!appts || appts.length === 0) return null;
                  const firstAppointment = appts[0];
                  const isSelected = CalendarStore.selectedShelfAppointmentId === firstAppointment.id;
                  const showAction =
                    isSelected &&
                    (firstAppointment.address?.type === "Haus" || firstAppointment.address?.type === "Heim");
                  // Prefer the patient object that carries a name: the shelf's
                  // `patient` prop is often a bare { id } from the controller,
                  // which is enough for drag payloads but not for the drag
                  // ghost label. The Rx patient has the same id plus the name.
                  const rxPatient = firstAppointment.timeSlots?.[0]?.therapyRx?.rx
                    ?.patient as PatientDto | undefined;
                  const resolvedPatient = (patient?.lastName ? patient : rxPatient) ?? patient ?? rxPatient;
                  const patientIdNum = Number(resolvedPatient?.id);
                  const isCancelled = isCancelledAppointment(firstAppointment);
                  const sortedTimeSlots = slotsInChronologicalOrder(firstAppointment.timeSlots);
                  return (
                    <Box
                      key={firstAppointment.id}
                      className={cardShell()}
                      sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "stretch",
                        minWidth: "210px",
                        minHeight: 30,
                        flex: "0 0 auto",
                        overflow: "visible",
                        // Whole-appointment selection outlines the entire card
                        // (band selection language); at rest cardShell's own
                        // shadow applies, so no "none" here.
                        boxShadow: isSelected ? "0 0 0 2px #007bff" : undefined,
                      }}
                    >
                      {showAction && renderSelectionAction(isSelected, firstAppointment)}

                      {/* Group count: how many appointments of this type are stacked here.
                          Sits in the top-right corner of the whole-appointment box. */}
                      {appts.length > 1 && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 2,
                            // Left of the kebab, which owns the corner itself.
                            right: 24,
                            zIndex: 3,
                            minWidth: 16,
                            height: 16,
                            px: 0.5,
                            borderRadius: "8px",
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            lineHeight: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            pointerEvents: "none",
                          }}
                        >
                          {appts.length}
                        </Box>
                      )}

                      {/* Kebab in the top-right corner, like every other
                          appointment card in the system. The left rail it used
                          to share with the drag grip lost its second occupant
                          when the card body became the drag surface, and a
                          full-height column plus divider for one small button
                          was dead weight. The rows container reserves the
                          corner's width so no row's trailing icons sit under
                          it. */}
                      <Box sx={{ position: "absolute", top: 2, right: 2, zIndex: 3 }}>
                        <ShelfAppointmentMenu
                          appointment={firstAppointment}
                          patientId={patientIdNum}
                          isCancelled={isCancelled}
                        />
                      </Box>

                      {/* Per-timeslot rows: [slot grip][row body], siblings.
                          The row BODY carries the whole-appointment payload, so
                          grabbing anywhere on the card content drags the whole
                          appointment; the grip lifts just its slot. Single-slot
                          appointments get NO grip — slot and appointment would
                          be the same thing. No inner scrolling: the band sizes
                          to its tallest card (ShelfSection minHeight). */}
                      {/* Vertically centred with breathing room: a single-slot
                          card is one 18px row, and without the padding the
                          26px corner kebab stood taller than the card itself
                          and hung out of it. minHeight on the card (below)
                          guarantees the kebab always fits. */}
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          paddingRight: "22px",
                          py: "4px",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                        }}
                      >
                        {sortedTimeSlots.map((timeSlot, index) => {
                          const slotSelected = selectedSlotId === timeSlot.id;
                          return (
                            <Box
                              key={timeSlot.id ?? index}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.25,
                                px: 0.25,
                                borderRadius: "0.3em",
                                boxShadow: slotSelected ? "0 0 0 2px #007bff" : "none",
                              }}
                            >
                              {sortedTimeSlots.length > 1 && (
                                <Tooltip title="Einzelnen Slot ziehen" placement="left">
                                  <div>
                                    <DragSurface
                                      payload={adaptTimeSlotToDraggableEvent(firstAppointment, timeSlot, resolvedPatient, termDefaultDuration, termDefaultPassivity)}
                                      ghost={dragGhostLabel([timeSlot], resolvedPatient)}
                                      onClick={() => handleSlotClick(timeSlot.id!)}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                      }}
                                    >
                                      <DragIndicatorIcon sx={{ fontSize: 14, opacity: 0.5 }} />
                                    </DragSurface>
                                  </div>
                                </Tooltip>
                              )}
                              <DragSurface
                                payload={adaptAppointmentToDraggableEvent(firstAppointment, resolvedPatient, true, termDefaultDuration, termDefaultPassivity)}
                                ghost={dragGhostLabel(sortedTimeSlots, resolvedPatient)}
                                onClick={() => handleEventClick(firstAppointment.id!)}
                                style={{ flex: 1, minWidth: 0 }}
                              >
                                {/* No treatment index here: the card's corner
                                    badge already counts the stack, and a
                                    second number beside it read as a rival
                                    count of the same thing. */}
                                <TherapySlotRow
                                  timeSlot={timeSlot}
                                  appointment={firstAppointment}
                                  index={index}
                                  totalSlots={sortedTimeSlots.length}
                                  hideTreatmentIndex
                                />
                              </DragSurface>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                });

              // 'Ablage' (pinboard) cards use the SAME drag zones as the unscheduled
              // shelf: the card body drags/selects the whole pin, and multi-slot pins
              // additionally expose per-slot grips. Slot grips carry the atomic
              // scheduled-slot-move payload (the pinned appointment is still scheduled
              // in the backend — the lift was client-side only). Per spec, single-slot
              // pins (incl. "Nur diese" slot pins, which are single-slot by
              // construction) get NO slot grip — the whole card IS the slot.
              const renderAblageAppointments = (appointments: AppointmentDto[]) =>
                appointments.map((appointment) => {
                  const isSelected = CalendarStore.selectedShelfAppointmentId === appointment.id;
                  const showAction =
                    isSelected &&
                    (appointment.address?.type === "Haus" || appointment.address?.type === "Heim");
                  // Same name-preferring resolution as the unscheduled cards
                  // (see above) — pins can even belong to other patients.
                  const pinRxPatient = appointment.timeSlots?.[0]?.therapyRx?.rx
                    ?.patient as PatientDto | undefined;
                  const resolvedPatient =
                    (patient?.lastName ? patient : pinRxPatient) ?? patient ?? pinRxPatient;
                  // Slot pins ("Umziehen → Nur diese") hold a single, still-scheduled slot
                  // under the SLOT id; their drag payload must be the single-slot shape
                  // (host appointment id restored) so the drop routes to the atomic
                  // slot-level reschedule instead of a whole-appointment save.
                  const isSlotPin = !!(appointment as any).isSlotPin;
                  const wholeDragPayload = isSlotPin
                    ? {
                        ...adaptTimeSlotToDraggableEvent(
                          { ...appointment, id: (appointment as any).pinnedAppointmentId },
                          appointment.timeSlots![0]!,
                          resolvedPatient,
                          termDefaultDuration,
                          termDefaultPassivity
                        ),
                        isScheduledSlotMove: true,
                      }
                    : adaptAppointmentToDraggableEvent(appointment, resolvedPatient, true, termDefaultDuration, termDefaultPassivity);
                  const sortedTimeSlots = slotsInChronologicalOrder(appointment.timeSlots);
                  // The pin's WINDOW is a time, so it comes off a list sorted by TIME
                  // and nothing else. Since MD-411 the display list is chronological
                  // too, but it deliberately falls back to NAMING order once an
                  // appointment is unscheduled — which is exactly the state a shelf pin
                  // is usually in, so it cannot answer a question about time. Both used
                  // to be one list sorted by `therapyRx.order`, a column that is null in
                  // every environment, so the window came off an arbitrary slot.
                  const chronologicalTimeSlots = [...(appointment.timeSlots ?? [])].sort(
                    (a: any, b: any) =>
                      new Date(a.start ?? 0).getTime() - new Date(b.start ?? 0).getTime()
                  );
                  // The pin keeps its original schedule — show WHEN it was, so the user
                  // knows what they are moving. Slot pins show the slot's own window;
                  // whole pins fall back to the first slot when the cached DTO carries
                  // no appointment-level window.
                  const displayStart = isSlotPin
                    ? chronologicalTimeSlots[0]?.start
                    : appointment.start ?? chronologicalTimeSlots[0]?.start;
                  const displayEnd = isSlotPin
                    ? chronologicalTimeSlots[0]?.end
                    : appointment.end ??
                      chronologicalTimeSlots[chronologicalTimeSlots.length - 1]?.end;
                  // Suppress the label entirely rather than render the
                  // formatter's "Invalid Date" fallback on unparseable input.
                  const pinDateLabel = (() => {
                    if (!displayStart) return "";
                    const label = formatDateDisplay(displayStart as any);
                    return label === "Invalid Date" ? "" : label;
                  })();
                  return (
                    <Box
                      key={appointment.id}
                      className={cardShell()}
                      sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "stretch",
                        minWidth: "210px",
                        flex: "0 0 auto",
                        overflow: "visible",
                        boxShadow: isSelected ? "0 0 0 2px #007bff" : undefined,
                      }}
                    >
                      {showAction && renderSelectionAction(isSelected, appointment)}

                      {/* Body: original schedule header + per-slot rows. The
                          header and every row body carry the whole-pin payload
                          (grab anywhere to move the pin); grips lift single
                          slots. No left rail — its only job was the grip. No
                          inner scrolling: the band sizes to its tallest card
                          (ShelfSection minHeight). */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <DragSurface
                          payload={wholeDragPayload}
                          ghost={dragGhostLabel(sortedTimeSlots, resolvedPatient)}
                          onClick={() => handleEventClick(appointment.id!)}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 4,
                            padding: "0 4px",
                          }}
                        >
                          <Typography sx={{ fontSize: "0.75em", fontWeight: "bold" }}>
                            {pinDateLabel}
                          </Typography>
                          <AppointmentTime
                            className="text-mini font-bold tabular-nums"
                            start={displayStart}
                            end={displayEnd}
                            label="Termin"
                          />
                        </DragSurface>
                        {sortedTimeSlots.map((timeSlot, index) => {
                          const slotSelected = selectedSlotId === timeSlot.id;
                          return (
                            <Box
                              key={timeSlot.id ?? index}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.25,
                                px: 0.25,
                                borderRadius: "0.3em",
                                boxShadow: slotSelected ? "0 0 0 2px #007bff" : "none",
                              }}
                            >
                              {sortedTimeSlots.length > 1 && (
                                <Tooltip title="Einzelnen Slot ziehen" placement="left">
                                  <div>
                                    <DragSurface
                                      payload={{
                                        ...adaptTimeSlotToDraggableEvent(appointment, timeSlot, resolvedPatient, termDefaultDuration, termDefaultPassivity),
                                        isScheduledSlotMove: true,
                                      }}
                                      ghost={dragGhostLabel([timeSlot], resolvedPatient)}
                                      onClick={() => handleSlotClick(timeSlot.id!)}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                      }}
                                    >
                                      <DragIndicatorIcon sx={{ fontSize: 14, opacity: 0.5 }} />
                                    </DragSurface>
                                  </div>
                                </Tooltip>
                              )}
                              <DragSurface
                                payload={wholeDragPayload}
                                ghost={dragGhostLabel(sortedTimeSlots, resolvedPatient)}
                                onClick={() => handleEventClick(appointment.id!)}
                                style={{ flex: 1, minWidth: 0 }}
                              >
                                <TherapySlotRow
                                  timeSlot={timeSlot}
                                  appointment={appointment}
                                  index={index}
                                  totalSlots={sortedTimeSlots.length}
                                />
                              </DragSurface>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                });

              return mode === "pinboard" ? renderAblageAppointments(ablageAppointments) : renderDefaultGroups(groupAppointments);
            })()}
    </ShelfSection>
  );
});

/** MINIMUM height of the band's card row (px) — fits ~3 compact therapy-slot
    rows; taller content scrolls inside its card. Badges live inside card
    corners, so the row needs no overhang allowance. NOTHING inside the band
    may grow it. */
const SHELF_ROW_HEIGHT = 65;

/**
 * Which side of the calendar grid the shelf is rendered on. Drives the order
 * of the two blocks (drag sources always end up nearest the grid) and the
 * direction the strip popovers open.
 */
export type ShelfPlacement = "above" | "below";

/** Strip popovers open AWAY from the calendar, so the expanded list never
    covers the grid: downward above it, upward below it. Without this a strip
    sitting at the bottom of the viewport would have its popover clamped back
    over the strip itself (MUI keeps popovers inside the viewport but does not
    flip them). */
const stripPopoverOrigins = (placement: ShelfPlacement) =>
  placement === "below"
    ? {
        anchorOrigin: { vertical: "top", horizontal: "left" } as const,
        transformOrigin: { vertical: "bottom", horizontal: "left" } as const,
      }
    : {
        anchorOrigin: { vertical: "bottom", horizontal: "left" } as const,
        transformOrigin: { vertical: "top", horizontal: "left" } as const,
      };

/**
 * One-line patient-notes strip alongside the planning band. ALL notes run one
 * after another on the single line (newest first, ticker-style but static);
 * whatever overflows is ellipsized. Clicking the strip opens the full,
 * scrollable list — avatars, dates and full text live only in that expanded
 * popover, never in the strip. Long notes therefore can never drive the
 * shelf height (the old notes panel stretched the whole row to fit the
 * tallest note — even one scrolled out of view).
 */
const NotesStrip = ({ notes, placement }: { notes: NoteDto[]; placement: ShelfPlacement }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const sorted = [...notes].sort(
    (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
  );
  if (sorted.length === 0) return null;
  const countLabel = notes.length === 1 ? "1 Notiz" : `${notes.length} Notizen`;
  const ExpandIcon = placement === "below" ? KeyboardArrowUpIcon : KeyboardArrowDownIcon;
  return (
    <>
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.5,
          minWidth: 0,
          borderRadius: "10px",
          border: `1px solid ${mobiliTheme.mainHeader}`,
          backgroundColor: mobiliTheme.topLevelBackground,
          cursor: "pointer",
          "&:hover": { backgroundColor: mobiliTheme.cardBackground },
        }}
      >
        <Badge badgeContent={notes.length} color="secondary" max={99}>
          <StickyNote2OutlinedIcon fontSize="small" />
        </Badge>
        {/* noWrap makes the parent a single ellipsized line; note texts render
            as inline spans separated by dots (newlines collapse to spaces). */}
        <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
          {sorted.map((note, i) => (
            <Box component="span" key={note.id ?? i}>
              {i > 0 && (
                <Box component="span" sx={{ mx: 1, opacity: 0.4 }}>
                  •
                </Box>
              )}
              {note.text}
            </Box>
          ))}
        </Typography>
        <Typography variant="caption" noWrap sx={{ flexShrink: 0, fontWeight: 600 }}>
          {countLabel}
        </Typography>
        <ExpandIcon fontSize="small" sx={{ flexShrink: 0, opacity: 0.7 }} />
      </Box>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        {...stripPopoverOrigins(placement)}
      >
        <Box
          sx={{
            p: 1.5,
            width: 420,
            maxWidth: "80vw",
            maxHeight: 320,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {sorted.map((note) => (
            <Box key={note.id} sx={{ display: "flex", gap: 1 }}>
              <MobiliAvatar
                user={note.updatedByUser || note.createdByUser}
                sx={{ width: 24, height: 24, fontSize: "0.65rem", mt: 0.25 }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                {note.createdAt && (
                  <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>
                    {formatDateDisplay(new Date(note.createdAt))} {formatTime(note.createdAt)}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {note.text}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};

/**
 * Therapist wish-list strip (gewünschte / nicht gewünschte Therapeut:innen),
 * styled like NotesStrip: shows the first two entries (+N for the rest) within
 * a fixed max width; clicking opens the full Muss/Kann list in a popover.
 */
const TherapistStrip = ({
  therapists,
  placement,
}: {
  therapists: { key?: string; name: string; listType?: TherapistListType; isMandatory: boolean }[];
  placement: ShelfPlacement;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  if (therapists.length === 0) return null;
  const visibleTherapists = therapists.slice(0, 2);
  const moreCount = therapists.length - visibleTherapists.length;
  const ExpandIcon = placement === "below" ? KeyboardArrowUpIcon : KeyboardArrowDownIcon;
  return (
    <>
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1.5,
          py: 0.5,
          minWidth: 0,
          maxWidth: 360,
          borderRadius: "10px",
          border: `1px solid ${mobiliTheme.mainHeader}`,
          backgroundColor: mobiliTheme.topLevelBackground,
          cursor: "pointer",
          "&:hover": { backgroundColor: mobiliTheme.cardBackground },
        }}
      >
        <Badge badgeContent={therapists.length} color="primary" max={99} sx={{ mr: 0.5 }}>
          <PeopleAltOutlinedIcon fontSize="small" />
        </Badge>
        {/* green = gewünscht, red = nicht gewünscht, bold = Muss */}
        <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
          {visibleTherapists.map((t, i) => {
            const blocked = t.listType === TherapistListType.Blacklist;
            const WishIcon = blocked ? DoNotDisturbAltIcon : CheckCircleOutlineIcon;
            return (
              <Box component="span" key={t.key ?? i}>
                {i > 0 && (
                  <Box component="span" sx={{ mx: 1, opacity: 0.4 }}>
                    •
                  </Box>
                )}
                <Box
                  component="span"
                  sx={{
                    color: blocked ? "error.dark" : "success.dark",
                    fontWeight: t.isMandatory ? 700 : 400,
                    whiteSpace: "nowrap",
                  }}
                >
                  <WishIcon sx={{ fontSize: "0.9em", verticalAlign: "-0.1em", mr: 0.25 }} />
                  {t.name}
                </Box>
              </Box>
            );
          })}
        </Typography>
        {moreCount > 0 && (
          <Typography variant="caption" noWrap sx={{ flexShrink: 0, fontWeight: 600, opacity: 0.8, ml: 0.5 }}>
            +{moreCount}
          </Typography>
        )}
        <ExpandIcon fontSize="small" sx={{ flexShrink: 0, opacity: 0.7, ml: moreCount > 0 ? 0 : -0.25 }} />
      </Box>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        {...stripPopoverOrigins(placement)}
      >
        <Box
          sx={{
            p: 1.5,
            width: 320,
            maxWidth: "80vw",
            maxHeight: 320,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 0.25,
          }}
        >
          {therapists.map((t, i) => {
            const blocked = t.listType === TherapistListType.Blacklist;
            const WishIcon = blocked ? DoNotDisturbAltIcon : CheckCircleOutlineIcon;
            return (
              <Box
                key={t.key ?? i}
                sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 0.5, py: 0.25, borderRadius: "6px" }}
              >
                {/* Muss/Kann tag: solid border = Muss, dashed = Kann */}
                <Typography
                  sx={{
                    width: 38,
                    flexShrink: 0,
                    textAlign: "center",
                    fontSize: "0.55rem",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    lineHeight: 1.5,
                    textTransform: "uppercase",
                    borderRadius: "4px",
                    border: t.isMandatory ? "1px solid" : "1px dashed",
                    borderColor: blocked ? "error.dark" : "success.dark",
                    color: blocked ? "error.dark" : "success.dark",
                    backgroundColor: blocked ? "rgba(211, 47, 47, 0.08)" : "rgba(46, 125, 50, 0.08)",
                  }}
                >
                  {t.isMandatory ? "Muss" : "Kann"}
                </Typography>
                <WishIcon
                  sx={{ fontSize: "0.85rem", flexShrink: 0, color: blocked ? "error.dark" : "success.dark" }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.75rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {t.name}
                </Typography>
                <Typography variant="caption" noWrap sx={{ ml: "auto", pl: 1, opacity: 0.7, flexShrink: 0 }}>
                  {blocked ? "Nicht gewünscht" : "Gewünscht"}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};

interface EventShelfProps {
  /**
   * Which side of the calendar grid the shelf sits on. Defaults to "above"
   * (the old Scheduler's arrangement); MobiliScheduler passes "below" so the
   * grid stays top-anchored when a patient's optional context appears.
   */
  placement?: ShelfPlacement;
  /**
   * "band" (default): the horizontal strip next to the calendar grid.
   * "column": the drawer prototype — sections stack vertically, cards wrap to
   * the container width instead of scrolling horizontally. Same controller,
   * same drag payloads, same Draggable registration; only the layout differs.
   */
  layout?: "band" | "column";
}

// Main EventShelf component - all data comes from the controller; the only
// prop is where the shelf sits relative to the grid.
const EventShelf: React.FC<EventShelfProps> = observer(({ placement = "above", layout = "band" }) => {
  const { shelves, patientNotes, therapistList, hasPatient, handleSelectEvent, handleCloseShelf, isLoading, continuousView } = useEventShelfController();
  const CalendarStore = useStore(CalendarContext);
  // Escape handling for the shelf. Registered manually because the central
  // hotkey system treats Escape as a buffer-clearing navigation key and never
  // dispatches it. Contract: molecules/Calendar/hotkeys/keyboardSpec.ts →
  // escapeStagedClear (both calendar engines inherit this by rendering the
  // shelf). Staged clear, in priority order:
  //   1. Cancel the client-side "Umziehen" pinboard — clearing it restores the
  //      lifted events on the calendar (nothing was persisted).
  //   2. Clear the drawer's secondary (non-target) patient, falling back to the
  //      target tab.
  //   3. Clear the shelf selection (appointment or template).
  const hasPinBoard = shelves.some((shelf) => shelf.mode === "pinboard");
  const hasShelfSelection = !!(
    CalendarStore.selectedShelfAppointmentId || CalendarStore.selectedShelfTemplate
  );
  const hasSecondaryPatient = CalendarStore.secondaryTabPatientId != null;
  useEffect(() => {
    if (!hasPinBoard && !hasShelfSelection && !hasSecondaryPatient) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Don't steal Escape from open dialogs / menus / popovers.
      const target = e.target as HTMLElement | null;
      if (target?.closest?.(".MuiModal-root, .MuiPopover-root, .MuiDialog-root")) return;
      if (CalendarStore.pinBoardItems.length > 0) {
        handleCloseShelf();
        return;
      }
      if (CalendarStore.clearSecondaryPatient()) return;
      if (CalendarStore.selectedShelfAppointmentId || CalendarStore.selectedShelfTemplate) {
        CalendarStore.setSelectedShelfAppointment(null, null);
        CalendarStore.setSelectedShelfTemplate(null);
        return;
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [hasPinBoard, hasShelfSelection, hasSecondaryPatient, handleCloseShelf, CalendarStore]);
  const {
    handles: templateHandles,
    isOpen: templateOpen,
    canSeeFeaturePreview: canSeeTemplate,
    patientId: templatePatientId,
    selectableRxs,
    defaultSelection,
    buildManualHandle,
    buildManualBundles,
    toggleOpen: toggleTemplate,
  } = useDynamicTemplateProviderController();
  const showNotes = hasPatient && patientNotes.length > 0;
  const showTherapistList = hasPatient && therapistList.length > 0;
  // Vorlagen visibility follows three scenarios:
  //  1. Nothing to plan at all → no band; the expander lives in the
  //     HeaderToolbar (costs no vertical space).
  //  2. Band visible via Offene Termine/Ablage but no auto handles → a slim
  //     "Vorlagen ›" stub at the band's left edge expands the selection mode.
  //  3. Auto handles present → clean chips; the section header offers a ›
  //     toggle into manual Rx/therapy selection mode.
  const showTemplateSection =
    canSeeTemplate && hasPatient && (templateHandles.length > 0 || templateOpen);
  // Manual selection mode is ADDITIVE: when summoned it renders alongside any
  // auto handles instead of being mutually exclusive with them.
  const showManualPicker = templateOpen;
  const showVorlagenStub =
    canSeeTemplate && hasPatient && !showTemplateSection && shelves.length > 0;
  const showBand = showTemplateSection || shelves.length > 0;

  // ONE FullCalendar Draggable for the whole band — template chips and shelf
  // card grips are all `.fc-event` descendants of the band element. Replaces
  // the old per-shelf/per-widget registrations (duplicate container ids, and
  // the shelf one was never destroyed, stacking Draggables on every remount).
  // CALLBACK REF, deliberately: with a plain ref + [showBand] deps the effect
  // could fire during a loading render (component returns null, ref still
  // null) and never again if showBand stayed true — grips then show the grab
  // cursor (CSS) but dragging is dead because no Draggable was ever attached.
  const [bandEl, setBandEl] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!bandEl) return;
    const draggable = new DraggableEvent(bandEl, {
      itemSelector: ".fc-event",
      eventData: (eventEl) => {
        const json = JSON.parse(eventEl.getAttribute("data-event")!);
        return {
          // Shelf grips carry no text; template payloads bring their own title.
          title: json.title ?? "",
          duration: json.duration,
          id: json.id,
          patient: json.patient,
          timeSlots: json.timeSlots,
          frequency: json.frequency,
          address: json.address,
          // Slot-level discriminators: without forwarding these, single-slot
          // payloads are indistinguishable from whole-appointment drops and the
          // drop handler would route them to the wrong verb.
          isSingleTimeSlot: json.isSingleTimeSlot,
          isScheduledSlotMove: json.isScheduledSlotMove,
          // Template discriminators for the create-and-schedule drop path.
          isTemplate: json.isTemplate,
          template: json.template,
        };
      },
    });
    return () => draggable.destroy();
  }, [bandEl]);

  if (isLoading || (!showNotes && !showTherapistList && !showBand)) return null;

  // Assemble the band sections in planning order (mirrors the "a" hotkey
  // cycle): Vorlagen → Offene Termine → Ablage, separated by thin dividers.
  const sections: React.ReactNode[] = [];
  const pushSection = (key: string, node: React.ReactNode) => {
    if (sections.length > 0) {
      sections.push(
        <Divider
          key={`divider-${key}`}
          orientation={layout === "column" ? "horizontal" : "vertical"}
          flexItem
          sx={{
            borderColor: "rgba(0, 48, 89, 0.3)",
            ...(layout === "column" ? { mx: 1 } : { my: 1 }),
          }}
        />
      );
    }
    sections.push(node);
  };
  if (showTemplateSection) {
    pushSection(
      "vorlagen",
      <DynamicTemplateProvider
        key="vorlagen"
        handles={templateHandles}
        patientId={templatePatientId ?? undefined}
        showManualPicker={showManualPicker}
        selectableRxs={selectableRxs}
        defaultSelection={defaultSelection}
        buildManualHandle={buildManualHandle}
        buildManualBundles={buildManualBundles}
        open={templateOpen}
        onToggleOpen={toggleTemplate}
        rowHeight={SHELF_ROW_HEIGHT}
        wrap={layout === "column"}
      />
    );
  } else if (showVorlagenStub) {
    // Scenario 2: the band is on screen for Offene Termine/Ablage, but there
    // are no Vorlagen to show. A slim stub in the section-header line keeps
    // the manual selection mode reachable exactly where it would appear —
    // without reserving a whole section (or defaulting into selection mode,
    // which would wrongly suggest something there MUST be scheduled).
    pushSection(
      "vorlagen-stub",
      <Tooltip key="vorlagen-stub" title="Vorlagen einblenden" placement="top" arrow>
        <Box
          onClick={toggleTemplate}
          sx={{
            display: "flex",
            alignItems: "center",
            alignSelf: "flex-start",
            gap: 0.25,
            height: 28,
            mt: 0.25,
            px: 1,
            flexShrink: 0,
            cursor: "pointer",
            opacity: 0.6,
            "&:hover": { opacity: 1 },
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Vorlagen
          </Typography>
          <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />
        </Box>
      </Tooltip>
    );
  }
  shelves.forEach((shelfConfig, index) => {
    pushSection(
      shelfConfig.mode ?? String(index),
      <ShelfInstance
        key={`shelf-${shelfConfig.mode ?? index}`}
        appointments={shelfConfig.appointments}
        title={shelfConfig.title}
        patient={shelfConfig.patient}
        draggable={shelfConfig.draggable}
        closeButton={shelfConfig.closeButton}
        mode={shelfConfig.mode}
        isContinuous={continuousView}
        rowHeight={SHELF_ROW_HEIGHT}
        grow
        wrap={layout === "column"}
        onSelectEvent={handleSelectEvent}
        onClose={shelfConfig.closeButton ? handleCloseShelf : undefined}
      />
    );
  });

  const stripsBlock = (showNotes || showTherapistList) && (
    // Notes take the remaining width; the therapist strip caps its own
    // maxWidth so it can never cover the notes.
    <Box key="strips" sx={{ display: "flex", gap: 0.5, minWidth: 0 }}>
      {showNotes && (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <NotesStrip notes={patientNotes} placement={placement} />
        </Box>
      )}
      {showTherapistList && (
        <Box sx={{ flex: "0 1 auto", minWidth: 0 }}>
          <TherapistStrip therapists={therapistList} placement={placement} />
        </Box>
      )}
    </Box>
  );

  const bandBlock = showBand && (
    // The planning band: ONE shared surface with a fixed height. Sections
    // (Vorlagen / Offene Termine / Ablage) have no boxes of their own —
    // only the draggable cards inside them read as objects.
    <Box
      key="band"
      ref={setBandEl}
      id="external-events"
      sx={{
        display: "flex",
        flexDirection: layout === "column" ? "column" : "row",
        alignItems: "stretch",
        minWidth: 0,
        borderRadius: "10px",
        border: `1px solid ${mobiliTheme.mainHeader}`,
        backgroundColor: mobiliTheme.topLevelBackground,
      }}
    >
      {sections}
    </Box>
  );

  // Below the grid the order flips, so the draggable cards stay adjacent to
  // the calendar they are dragged onto and the read-only strips sit outermost.
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
      {placement === "below" ? [bandBlock, stripsBlock] : [stripsBlock, bandBlock]}
    </Box>
  );
});

export default EventShelf;
